import { CartItem } from '../../types/cart'
import { RoomForSearch } from '../../types/searchAvailability'
import { SelectedWalletType } from '../../types/wallet'

export const transformRoomsWithCart = (
  rooms: RoomForSearch[],
  cartItems: CartItem[]
): RoomForSearch[] =>
  rooms.map((room) => ({
    ...room,
    ratePlans: room.ratePlans.map((rp) => {
      const matchingItem = cartItems.find((item) => item.hash === rp.hash)
      return {
        ...rp,
        count: matchingItem?.quantity || 0
      }
    })
  }))

/**
 * Apply search's verdict for the selected wallet to every rate plan.
 *
 * The panel used to decide this itself: it fetched the wallet's creditors and compared
 * each creditor's provider code against `ratePlan.displayProviderCode`. Those are two
 * different code spaces. A channel whose ShowProviderName is false is never told a
 * provider's identity -- search returns the RATE GROUP code as the public handle -- so
 * the comparison could not match, and every rate plan was reported as having no credit
 * provider while payment through Adopay worked normally.
 *
 * Search now decides, because it is the only component holding both the true provider
 * code and the channel's flag. The creditor codes it compares come from the wallet
 * service, never from this client: the comparison's result is returned, so a caller
 * able to supply the codes could submit one guess per request and read the verdict to
 * learn whether it matched -- recovering the identity the flag exists to hide.
 *
 * Verdicts for every wallet arrive in one response, so switching wallet or payment
 * method re-reads them without a refetch.
 *
 * A rate plan judged unusable is DISABLED, not removed: the operator is meant to see
 * what the other wallet could buy and choose with open eyes.
 */
export const transformRatePlansWithWallets = (
  rooms: RoomForSearch[],
  selectedWalletType: SelectedWalletType
): RoomForSearch[] => {
  if (!selectedWalletType.walletID) {
    return rooms
  }

  return rooms.map((room) => ({
    ...room,
    ratePlans: room.ratePlans.map((ratePlan) => {
      const verdict = ratePlan.walletEligibilities?.find(
        (e) => e.walletID === selectedWalletType.walletID
      )

      // No verdict is not a refusal. Search judges only the wallets it was asked
      // about, and it is asked nothing when the wallet service is unreachable --
      // degrading to the unjudged result rather than disabling the whole page.
      if (!verdict) {
        return ratePlan
      }

      return {
        ...ratePlan,
        bookabilityStatus: verdict.bookabilityStatus,
        disable: verdict.bookabilityStatus !== 'BOOKABLE'
      }
    })
  }))
}
